import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BACKEND_API_BASE_URL } from '../config/app-settings.tokens';
import { RequestStatus } from '../models/request-enums';
import { Request } from '../models/request-models';

/**
 * Pagination Info Interface
 */
export interface PaginationInfo {
  pageSize: number;
  pageNumber: number;
}

/**
 * Request Task Interface
 */
export interface RequestTask {
  taskId: string;
  taskDueDate: string;
  taskReceivedDate: string;
  taskClosedDate: string | null;
  comment: string | null;
  sentFrom: string;
  id: string;
  number: string;
  departmentId: string;
  module: string;
  regardingType: number;
  regardingId: string | null;
  parentRequest: string | null;
  createdBy: { userId: string; name: string | null };
  modifiedBy: { userId: string; name: string | null };
  createdDate: string;
  modifiedDate: string;
  submittedUtc: string | null;
  typeLogicalId: string;
  status: string;
  lockedByUserId: { userId: string | null; name: string | null };
  lockedByUser: string;
  sentToUser: boolean;
  sentToStructure: boolean;
  year: number;
}

/**
 * Requests Response Interface
 */
export interface RequestsResponse {
  pageSize: number;
  pageNumber: number;
  totalRecords: number;
  data: RequestTask[];
}

@Injectable({
  providedIn: 'root'
})
export class RequestManagementService {
  private baseUrl = `${inject(BACKEND_API_BASE_URL)}`;

  constructor(private http: HttpClient) {}

  /**
   * Fetch user inbox requests
   * @param info - PaginationInfo with pageSize and pageNumber
   * @param delegationId - Optional delegation ID
   * @returns Observable of RequestsResponse containing requests data
   */
  getInbox(info: PaginationInfo, delegationId?: number): Observable<RequestsResponse> {
    let params = new HttpParams()
      .set('pageSize', info.pageSize.toString())
      .set('pageNumber', info.pageNumber.toString());

    if (delegationId !== undefined) {
      params = params.set('delegationId', delegationId.toString());
    }

    const url = `${this.baseUrl}/request/user/inbox`;
    return this.http.get<RequestsResponse>(url, { params });
  }

  /**
   * Fetch user sent requests
   * @param info - PaginationInfo with pageSize and pageNumber
   * @param delegationId - Optional delegation ID
   * @returns Observable of RequestsResponse containing requests data
   */
  getSent(info: PaginationInfo, delegationId?: number): Observable<RequestsResponse> {
    let params = new HttpParams()
      .set('pageSize', info.pageSize.toString())
      .set('pageNumber', info.pageNumber.toString());

    if (delegationId !== undefined) {
      params = params.set('delegationId', delegationId.toString());
    }

    const url = `${this.baseUrl}/request/user/task/executed`;
    return this.http.get<RequestsResponse>(url, { params });
  }

  /**
   * Fetch user completed requests
   * @param info - PaginationInfo with pageSize and pageNumber
   * @param delegationId - Optional delegation ID
   * @returns Observable of RequestsResponse containing requests data
   */
  getCompleted(info: PaginationInfo, delegationId?: number): Observable<RequestsResponse> {
    let params = new HttpParams()
      .set('pageSize', info.pageSize.toString())
      .set('pageNumber', info.pageNumber.toString());

    if (delegationId !== undefined) {
      params = params.set('delegationId', delegationId.toString());
    }

    const url = `${this.baseUrl}/request/user/task/colsed`;
    return this.http.get<RequestsResponse>(url, { params });
  }

  /**
   * Fetch user's own sent requests
   * @param info - PaginationInfo with pageSize and pageNumber
   * @param delegationId - Optional delegation ID
   * @returns Observable of RequestsResponse containing requests data
   */
  getMySent(info: PaginationInfo, delegationId?: number): Observable<RequestsResponse> {
    let params = new HttpParams()
      .set('pageSize', info.pageSize.toString())
      .set('pageNumber', info.pageNumber.toString());

    if (delegationId !== undefined) {
      params = params.set('delegationId', delegationId.toString());
    }

    const url = `${this.baseUrl}/request/user/task/my`;
    return this.http.get<RequestsResponse>(url, { params });
  }

  /**
   * Fetch user's draft requests
   * @param info - PaginationInfo with pageSize and pageNumber
   * @param delegationId - Optional delegation ID
   * @param requestType - Optional filter by request type
   * @returns Observable of RequestsResponse containing draft requests
   */
  getDraftRequests(
    info: PaginationInfo,
    delegationId?: number,
    requestType?: any
  ): Observable<RequestsResponse> {
    let params = new HttpParams()
      .set('pageSize', info.pageSize.toString())
      .set('pageNumber', info.pageNumber.toString())
      .set('requestStatus', 'Draft');

    if (delegationId !== undefined) {
      params = params.set('delegationId', delegationId.toString());
    }

    if (requestType !== undefined) {
      params = params.set('requestType', requestType.toString());
    }

    const url = `${this.baseUrl}/request/user`;
    return this.http.get<RequestsResponse>(url, { params });
  }

  /**
   * Fetch request details by ID
   * @param requestId - The ID of the request to fetch
   * @returns Observable of Request containing full request details
   */
  getRequestById(requestId: string): Observable<Request> {
    const url = `${this.baseUrl}/request/id/${requestId}`;
    return this.http.get<Request>(url);
  }

  /**
   * Delete a draft request
   * @param requestId - The ID of the draft request to delete
   * @returns Observable of any (API response)
   */
  deleteDraft(requestId: string): Observable<any> {
    const url = `${this.baseUrl}/request/Draft/${requestId}`;
    return this.http.delete(url);
  }

}
